import { IconButton, makeStyles } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@material-ui/core";
import { Combobox, ComboboxInput, ComboboxList, ComboboxOption, ComboboxOptionText, ComboboxPopover } from "@reach/combobox";
import SearchIcon from '@material-ui/icons/Search';
import { Cross } from "./C3ImageLibrary";
import { MutableRefObject } from "react-transition-group/node_modules/@types/react";

const Configuration = require("shared/services/Configuration");
const Storage       = require("shared/services/Storage");

interface ImageLibrarySearchbarProps {
  handleSearch: (searchValue: string) => void;
  placeholder: string;
  fetchLimit: number;
  sortBy: string;
  folderPath: string;
}

const fetchLimit = 20;

const useStyles: any = makeStyles(() => ({
  searchBarContainer: {
    width: "100%",
  },
  searchBar: {
    display: "flex",
    background: "#FFFFFF",
    height: "32px",
    border: "1px solid #94A3B8",
    borderRadius: "4px",
    "&:focus-within": {
      outline: "-webkit-focus-ring-color auto 1px",
    }
  },
  imageSearchInput: {
    width: 392,
    height: 28,
    border: 0,
    outline: 0,
    backgroundColor: "#FFFFFF !important",
    color: "#000000 !important"
  },
  comboBoxMenu: {
    background: "#FFFFFF",
    zIndex: 2,
    borderRadius: "3px",
    boxShadow: "0px 3px 5px rgb(9 30 66 / 20%), 0px 0px 1px rgb(9 30 66 / 31%)",
  },
  comboBoxMenuList: {
    lineHeight: "3.65vh",
    fontSize: "0.8em",
    listStyle: "none",
    paddingLeft: "1.11vw",
    cursor: "pointer",
  },
}));

const ImageLibrarySearchbar = ({
  ...props
}: ImageLibrarySearchbarProps) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [matchedImageTags, setMatchedImageTags] = useState<any[]>([]);
  const searchCache: any = useRef({});
  const isMenuWidthIncreased: MutableRefObject<boolean> = useRef<boolean>(false);
  const timeout: any = useRef(null);

  useEffect(() => {
    onSearchInputChange();
    // Adjust the widht of the search menu to cover the whole input box since adding close icon misaligns the menu
    if (!isMenuWidthIncreased.current
        && document.getElementById("combo-box-popover")
        && !document.getElementById("combo-box-popover").hidden) {
      const newWidth = parseInt(document.getElementById("combo-box-popover").style.width) + 64 + "px";
      document.getElementById("combo-box-popover").style.width = newWidth;
      isMenuWidthIncreased.current = true;
    }
  }, [searchTerm]);

  useEffect(() => {
    setTimeout(() => {
      // Align Search Menu with input box since adding custom search icon misaligns reachUI popover (Menu)
      const newLeft = parseInt(document.getElementById("combo-box-popover").style.left) - 25 + "px";
      document.getElementById("combo-box-popover").style.left = newLeft;
    });
  }, []);

  const searchHandler = async () => {
    if (searchTerm.trim() !== "") {
      const res = await fetchMatchedImageTags(searchTerm);
      if (res.imageDetailsList.length) {
        let matchedTags: any[] = [];
        res.imageDetailsList.forEach((image: any) => {
          image.matchedTag && matchedTags.push(image.matchedTag);
        });

        setMatchedImageTags(matchedTags);
      } else {
        setMatchedImageTags([]);
      }
    }
  };

  const debounce = (func: Function, delay: number) => {
    return function mainFunction() {
      const wait = () => {
        clearTimeout(timeout.current);
        func();
      };
      clearTimeout(timeout.current);
      timeout.current = setTimeout(wait, delay);
    };
  };

  const onSearchInputChange = debounce(searchHandler, 500);

  const fetchMatchedImageTags = async (value: string) => {
    if (searchCache.current[value]) {
      return searchCache.current[value];
    }
    const res = await getImages(value);

    searchCache.current[value] = res;
    return res;
  };

  const getImages = async (searchPrefix: string) => {
    try {
    const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
    const res = await fetch(
        `${Configuration.c3ApibaseUrl}/v1/images?limit=${fetchLimit}&sortBy=${props.sortBy}&folderPath=${props.folderPath}${searchPrefix ? `&searchPrefix=${searchPrefix}` : ""}`,
        {
            headers: {
                Authorization: `Bearer ${token}`
            },
            method: "GET"
        }
      )

      return await res.json();
    } catch (e) {
        console.error(e);
        return false;
      }
};

  const getType = (type: string) => {
    switch (type) {
      case "IMGNAME":
        return "Image name";
      case "EXPNAME":
        return "Experience name";
      case "CNTSLOT":
        return "Content Slot";
      case "PGNAME":
        return "Page name";
      default:
        return "User Tag";
    }
  };

  return (
    <Grid item className={classes.searchBarContainer}>
      <Grid container  style={{ padding: "1vh 0vw", justifyContent: "space-between" }}>
          <Grid item className={classes.searchBarContainer}>
            <Combobox aria-label="images"
              onSelect={(value) => {
                setSearchTerm(value.split(" : ")[0]);
                // hide search menu
                document.getElementById("combo-box-popover").hidden = true;
                }}>
              <div className={classes.searchBar}>
                <IconButton
                style={{ paddingRight: "6px", paddingLeft: "6px" }}
                onClick={() => props.handleSearch(searchTerm)}>
                  <SearchIcon/>
                </IconButton>
                <ComboboxInput
                  value={searchTerm}
                  className={classes.imageSearchInput}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={props.placeholder}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      props.handleSearch(searchTerm);
                      e.currentTarget.blur();
                      // hide search menu
                      setTimeout(() => document.getElementById("combo-box-popover").hidden = true);
                    }
                  }}
                />
                <IconButton
                style={{ display: searchTerm ? "flex" : "none" }}
                onClick={() => {
                    setSearchTerm("");
                    props.handleSearch("")
                    }}>
                  <Cross/>
                </IconButton>
              </div>
              {matchedImageTags && (
                <ComboboxPopover id="combo-box-popover" className={classes.comboBoxMenu}>
                  {matchedImageTags.length > 0 && (
                    <ComboboxList className={classes.comboBoxMenuList}>
                      {matchedImageTags.map((imageTag) => {
                        const str = `${imageTag.tagText} : ${getType(imageTag.tagType)}`;
                        return <ComboboxOption key={str} value={str}><ComboboxOptionText /> </ComboboxOption>;
                      })}
                    </ComboboxList>
                  )}
                </ComboboxPopover>
              )}
            </Combobox>
          </Grid>
        </Grid>
    </Grid>
  );
};

export default ImageLibrarySearchbar;



// WEBPACK FOOTER //
// ./src/editor/ui/editor/imageLibrary/ImageLibrarySearchbar.tsx